import { motion } from "framer-motion";
import { Smartphone, CreditCard, Mail, MapPin, Heart, Copy, Check, ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import Seo from "../components/Seo";

const ways = [
  {
    title: "Cash App",
    handle: "$TPCTHEPUSHCENTER",
    desc: "Quick, contactless giving from your phone.",
    icon: Smartphone,
    link: "https://cash.app/$TPCTHEPUSHCENTER",
    linkLabel: "Open Cash App",
  },
  {
    title: "Givelify",
    handle: "The Push Center",
    desc: "Give in seconds — opens directly to our Givelify donation page.",
    icon: CreditCard,
    link: "https://www.givelify.com/donate/MTUwMjkwOQ==/selection",
    linkLabel: "Open Givelify",
  },
  {
    title: "PayPal",
    handle: "The Push Center",
    desc: "Send your gift through PayPal.",
    icon: CreditCard,
    link: "https://www.paypal.com/paypalme/thepushcenter",
    linkLabel: "Open PayPal",
  },
  {
    title: "Mail",
    handle: "6217 Portsmouth Blvd",
    desc: "Portsmouth, VA 23701 — mail a check.",
    icon: Mail,
  },
  {
    title: "In Person",
    handle: "Sundays · 9:00 AM",
    desc: "Drop your gift in the offering or scan the QR.",
    icon: MapPin,
  },
];

export default function Giving() {
  const [copied, setCopied] = useState(null);

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success(`Copied ${text}`);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="giving-page">
      <Seo
        title="Give"
        description="Generosity fuels ministry. Give to The Push Center via Cash App, Givelify, PayPal, mail, or in person."
        path="/giving"
      />
      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 lightning-grid opacity-30" />
        <div className="absolute top-20 left-1/3 w-96 h-96 bg-electric/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Generosity Fuels Ministry
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            Sow into <span className="italic text-electric text-pulse">destiny.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Your generosity pushes the gospel forward, equips ministries, and reaches lives. Our goal:
            100% tithe payers. See the Deacons Board for more information.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ways.map((w, i) => (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative p-8 rounded-2xl border border-white/5 bg-[#0a0a0a] hover:border-electric/50 transition-all"
              data-testid={`give-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-electric/10 border border-electric/20 flex items-center justify-center mb-6">
                <w.icon className="w-5 h-5 text-electric" />
              </div>
              <div className="font-display font-black text-2xl mb-2">{w.title}</div>
              <div className="text-electric font-bold text-sm mb-3 tracking-wider">{w.handle}</div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-5">{w.desc}</p>
              <div className="flex flex-wrap items-center gap-4">
                {w.link && (
                  <a
                    href={w.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-electric text-black px-4 py-2 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all"
                    data-testid={`give-link-${i}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {w.linkLabel || "Open"}
                  </a>
                )}
                {w.copy && (
                  <button
                    onClick={() => handleCopy(w.copy, w.title)}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-electric transition"
                    data-testid={`give-copy-${i}`}
                  >
                    {copied === w.title ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> {w.copyLabel || "Copy Handle"}</>}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-10 pb-32">
        <div className="relative p-10 lg:p-16 rounded-3xl bg-gradient-to-br from-electric/15 via-[#0c0c0c] to-[#0c0c0c] border border-electric/20">
          <div className="absolute inset-0 lightning-grid opacity-20 rounded-3xl" />
          <div className="relative flex flex-col lg:flex-row items-start gap-8">
            <Heart className="w-12 h-12 text-electric flex-shrink-0" />
            <div>
              <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-4">
                A word on tithing.
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-3">
                Tithing isn't about an institution — it's about honoring God with the firstfruits and
                trusting Him with the rest. As a body, we're pushing toward 100% tithe payers.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                Questions? The Deacons Board is available after every service to walk you through
                anything related to giving, stewardship, or finance.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
